import { Injectable } from '@angular/core';
import { of, timer, BehaviorSubject, Observable } from 'rxjs';
import { delay, last, mapTo, takeWhile, tap } from 'rxjs/operators';

export enum UploadStatus {
  Ready = 'Ready',
  Requested = 'Requested',
  Started = 'Started',
  Failed = 'Failed',
  Completed = 'Completed',
  Deleted = 'Deleted',
}

export interface State<T> {
  status: UploadStatus;
  error: string | null;
  progress: number | null;
  response: T | null;
  fileName: string | null;
}

export interface FileUpload {
  readonly fileName: string;
  readonly fileUrl: string;
}

export const initialState = {
  status: UploadStatus.Ready,
  error: null,
  progress: null,
  response: null,
  fileName: null,
};

/**
 * Dummy upload handler service to provide a dependency-free standin and
 * extensible interface for individual uploader instances, use provider
 * overrides at the module or component level to provide your own extended
 * version of this class for your use case.
 */
@Injectable({
  providedIn: 'root',
})
export class FileUploadService<T extends FileUpload = FileUpload> {
  public state$: Observable<State<T>>;

  private state: BehaviorSubject<State<T>>;

  constructor() {
    this.state = new BehaviorSubject<State<T>>(initialState);
    this.state$ = this.state.asObservable();
  }

  public upload(file: File): void {
    this.reduce({
      status: UploadStatus.Started,
      error: null,
      progress: null,
      fileName: file.name,
    });

    timer(1000, 50)
      .pipe(
        takeWhile((x) => x < 100),
        tap((x) => {
          this.reduce({
            progress: x,
            fileName: file.name,
          });
        }),
        last(),
        mapTo(<T>{ fileName: file.name })
      )
      .subscribe((response) => {
        this.reduce({
          status: UploadStatus.Completed,
          progress: 100,
          error: null,
          fileName: file.name,
          response,
        });
      });
  }

  public remove(file: T): void {
    this.reduce({
      status: UploadStatus.Started,
      error: null,
      progress: null,
      fileName: file.fileName,
    });

    of(file)
      .pipe(delay(1000))
      .subscribe((response) => {
        this.reduce({
          status: UploadStatus.Deleted,
          progress: 100,
          error: null,
          fileName: file.fileName,
          response,
        });

        this.reduce(initialState);
      });
  }

  protected reduce(state: Partial<State<T>>) {
    this.state.next({
      ...this.state.value,
      ...state,
    });
  }
}
